/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `PrivacyPolicy` for the Caintra Connect frontend.
 * @module pages/PrivacyPolicy
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import styles from "../styles/PrivacyPolicy.module.css";
import LanguageSelector from "../components/LanguageSelector/index.jsx";

import { ReactComponent as CaintraLogo } from "../assets/Caintra_Logo.svg";
import { ReactComponent as ChevronLeftIcon } from "../assets/icons/CompanyEditPage/ChevronLeftIcon.svg";

const PrivacyPolicy = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleBack = () => {
    const token = localStorage.getItem("token");
    if (token) {
      navigate("/home");
    } else {
      navigate("/");
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.returnButtonContainer} onClick={handleBack}>
          <ChevronLeftIcon className={styles.returnButtonIcon} />
          <span className={styles.returnButtonText}>{t("common.goBack")}</span>
        </button>
        <LanguageSelector />
      </div>
      <div className={styles.imgContainer}>
        <CaintraLogo className={styles.logo} />
      </div>
      <div className={styles.mainContainer}>
        <div>
          <span className={styles.title}>{t("pages.privacyPolicy.title")}</span>
        </div>
        <div className={styles.textWrapper}>
          <div className={styles.textContainer}>
            <h2 className={styles.sectionTitle}>
              {t("pages.privacyPolicy.term1Title")}
            </h2>
            <p className={styles.text}>{t("pages.privacyPolicy.term1Text")}</p>

            <h2 className={styles.sectionTitle}>
              {t("pages.privacyPolicy.term2Title")}
            </h2>
            <p className={styles.text}>{t("pages.privacyPolicy.term2Text1")}</p>
            <p className={styles.text}>{t("pages.privacyPolicy.term2Text2")}</p>

            <h2 className={styles.sectionTitle}>
              {t("pages.privacyPolicy.term3Title")}
            </h2>
            <p className={styles.text}>{t("pages.privacyPolicy.term3Text")}</p>

            <h2 className={styles.sectionTitle}>
              {t("pages.privacyPolicy.term4Title")}
            </h2>
            <p className={styles.text}>{t("pages.privacyPolicy.term4Text1")}</p>
            <p className={styles.text}>{t("pages.privacyPolicy.term4Text2")}</p>
            <p className={styles.text}>{t("pages.privacyPolicy.term4Text3")}</p>

            <h2 className={styles.sectionTitle}>
              {t("pages.privacyPolicy.term5Title")}
            </h2>
            <p className={styles.text}>{t("pages.privacyPolicy.term5Text1")}</p>
            <ol className={styles.orderedList}>
              <li className={styles.listItem}>
                a) {t("pages.privacyPolicy.term5Text2")}
              </li>
              <li className={styles.listItem}>
                b) {t("pages.privacyPolicy.term5Text3")}
              </li>
              <li className={styles.listItem}>
                c) {t("pages.privacyPolicy.term5Text4")}
              </li>
            </ol>

            <h2 className={styles.sectionTitle}>
              {t("pages.privacyPolicy.term6Title")}
            </h2>
            <p className={styles.text}>{t("pages.privacyPolicy.term6Text")}</p>

            <h2 className={styles.sectionTitle}>
              {t("pages.privacyPolicy.term7Title")}
            </h2>
            <p className={styles.text}>{t("pages.privacyPolicy.term7Text1")}</p>
            <p className={styles.text}>{t("pages.privacyPolicy.term7Text2")}</p>
            <p className={styles.text}>{t("pages.privacyPolicy.term7Text3")}</p>
            <p className={styles.text}>{t("pages.privacyPolicy.term7Text4")}</p>
            <p className={styles.text}>{t("pages.privacyPolicy.term7Text5")}</p>
            <p className={styles.text}>{t("pages.privacyPolicy.term7Text6")}</p>

            <h2 className={styles.sectionTitle}>
              {t("pages.privacyPolicy.term8Title")}
            </h2>
            <p className={styles.text}>{t("pages.privacyPolicy.term8Text1")}</p>
            <p className={styles.text}>{t("pages.privacyPolicy.term8Text2")}</p>

            <ol className={styles.orderedList}>
              <li className={styles.listItem}>
                (i) {t("pages.privacyPolicy.term8Text3")}
              </li>
              <li className={styles.listItem}>
                (ii) {t("pages.privacyPolicy.term8Text4")}
              </li>
              <li className={styles.listItem}>
                (iii) {t("pages.privacyPolicy.term8Text5")}
              </li>
              <li className={styles.listItem}>
                (iv) {t("pages.privacyPolicy.term8Text6")}
              </li>
              <li className={styles.listItem}>
                (v) {t("pages.privacyPolicy.term8Text7")}
              </li>
            </ol>

            <p className={styles.text}>{t("pages.privacyPolicy.term8Text8")}</p>
            <p className={styles.text}>{t("pages.privacyPolicy.term8Text9")}</p>
            <p className={styles.text}>
              {t("pages.privacyPolicy.term8Text10")}
            </p>

            <h2 className={styles.sectionTitle}>
              {t("pages.privacyPolicy.term9Title")}
            </h2>
            <p className={styles.text}>{t("pages.privacyPolicy.term9Text1")}</p>
            <p className={styles.text}>{t("pages.privacyPolicy.term9Text2")}</p>
            <p className={styles.bulletPoint}>
              - {t("pages.privacyPolicy.term9Text3")}
            </p>
            <p className={styles.bulletPoint}>
              - {t("pages.privacyPolicy.term9Text4")}
            </p>

            <h2 className={styles.sectionTitle}>
              {t("pages.privacyPolicy.term10Title")}
            </h2>
            <p className={styles.text}>
              {t("pages.privacyPolicy.term10Text1")}
            </p>

            <ol className={styles.orderedList}>
              <li className={styles.listItem}>
                a) {t("pages.privacyPolicy.term10Text2")}
              </li>
              <li className={styles.listItem}>
                b) {t("pages.privacyPolicy.term10Text3")}
              </li>
              <li className={styles.listItem}>
                c) {t("pages.privacyPolicy.term10Text4")}
              </li>
            </ol>

            <p className={styles.text}>
              {t("pages.privacyPolicy.term10Text5")}
            </p>
            <p className={styles.text}>
              {t("pages.privacyPolicy.term10Text6")}
            </p>
            <p className={styles.text}>
              {t("pages.privacyPolicy.term10Text7")}
            </p>

            <h2 className={styles.sectionTitle}>
              {t("pages.privacyPolicy.term11Title")}
            </h2>
            <p className={styles.text}>
              {t("pages.privacyPolicy.term11Text1")}
            </p>

            <ul className={styles.unorderedList}>
              <li className={styles.listItem}>
                {t("pages.privacyPolicy.term11Text2")}
              </li>
              <li className={styles.listItem}>
                {t("pages.privacyPolicy.term11Text3")}
              </li>
              <li className={styles.listItem}>
                {t("pages.privacyPolicy.term11Text4")}
              </li>
              <li className={styles.listItem}>
                {t("pages.privacyPolicy.term11Text5")}
              </li>
            </ul>

            <p className={styles.text}>
              {t("pages.privacyPolicy.term11Text6")}
            </p>
            <p className={styles.text}>
              {t("pages.privacyPolicy.term11Text7")}
            </p>
            <p className={styles.text}>
              {t("pages.privacyPolicy.term11Text8")}
            </p>

            <h2 className={styles.sectionTitle}>
              {t("pages.privacyPolicy.term12Title")}
            </h2>
            <p className={styles.text}>
              {t("pages.privacyPolicy.term12Text1")}
            </p>
            <p className={styles.text}>
              {t("pages.privacyPolicy.term12Text2")}
            </p>

            <h2 className={styles.sectionTitle}>
              {t("pages.privacyPolicy.term13Title")}
            </h2>
            <p className={styles.text}>{t("pages.privacyPolicy.term13Text")}</p>

            <h2 className={styles.sectionTitle}>
              {t("pages.privacyPolicy.term14Title")}
            </h2>
            <p className={styles.text}>
              {t("pages.privacyPolicy.term14Text1")}
            </p>
            <p className={styles.text}>
              {t("pages.privacyPolicy.term14Text2")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
